import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { usersApi } from '../../../api/usersApi';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 500,
  },
}));

const DropZone = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const PhotoUploadDialog = ({ open, onClose, onPhotoUploaded, currentPhoto, userId }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [previewImage, setPreviewImage] = useState(currentPhoto || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const validateImage = (file) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

    if (file.size > maxSize) {
      throw new Error('L\'image ne doit pas dépasser 2MB');
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Format d\'image non supporté. Utilisez JPG, PNG ou GIF');
    }

    return true;
  };

  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculer les nouvelles dimensions (max 300x300 pour l'avatar)
        const maxSize = 300;
        let { width, height } = img;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir en base64
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        resolve(base64);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = useCallback(async (file) => {
    try {
      setError('');
      setLoading(true);

      validateImage(file);
      const base64Image = await resizeImage(file);
      
      setPreviewImage(base64Image);
      setImageUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim()) {
      setError('Veuillez saisir une URL d\'image');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Télécharger l'image depuis l'URL
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Impossible de télécharger l\'image depuis cette URL');
      }

      const blob = await response.blob();
      const file = new File([blob], 'image.jpg', { type: blob.type });
      
      await handleFileSelect(file);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!previewImage) {
      setError('Veuillez sélectionner une image');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Extraire le base64 sans le préfixe data:image/...
      const base64Data = previewImage.split(',')[1];

      await usersApi.updateUserPhoto(
        localStorage.getItem('accessToken'),
        { photo: base64Data },
        userId
      );

      onPhotoUploaded(previewImage);
      onClose();
    } catch (err) {
      setError('Erreur lors de la sauvegarde de la photo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPreviewImage('');
    setImageUrl('');
    setError('');
  };

  const handleClose = () => {
    if (!loading) {
      setPreviewImage(currentPhoto || '');
      setImageUrl('');
      setError('');
      setIsDragOver(false);
      onClose();
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {currentPhoto ? 'Changer la photo' : 'Ajouter une photo'}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Prévisualisation */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Prévisualisation
              </Typography>
              <Avatar
                src={previewImage}
                sx={{
                  width: 150,
                  height: 150,
                  mx: 'auto',
                  mb: 2,
                  border: '2px solid',
                  borderColor: 'divider'
                }}
              />
              {previewImage && (
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={handleRemovePhoto}
                  color="error"
                  variant="outlined"
                  size="small"
                >
                  Supprimer
                </Button>
              )}
            </Box>
          </Grid>

          {/* Zone d'upload */}
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              Méthodes d'import
            </Typography>

            {/* Drag & Drop */}
            <DropZone
              isDragOver={isDragOver}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Glissez-déposez une image ici
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ou cliquez pour sélectionner un fichier
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Formats acceptés : JPG, PNG, GIF (max 2MB)
              </Typography>
            </DropZone>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OU
              </Typography>
            </Divider>

            {/* URL d'image */}
            <Typography variant="subtitle1" gutterBottom>
              Importer depuis une URL
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleUrlSubmit}
                disabled={loading || !imageUrl.trim()}
              >
                Importer
              </Button>
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !previewImage}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

PhotoUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPhotoUploaded: PropTypes.func.isRequired,
  currentPhoto: PropTypes.string,
  userId: PropTypes.string.isRequired,
};

export default PhotoUploadDialog; 